import { GVL, RestrictionType, TCModel, TCString } from '@iabtechlabtcf/core'
import { Vendor, vendors } from './defaults/vendor.enum'
import { Logger } from '../../utils/logging/logger'
import { initDefaultTcfVendorsConsentsMap } from './defaults/init-default-tcf-vendors-consents-map'
import { TcfApi } from './tcf-api/tcf-api.type'
import { Purpose } from './defaults/purpose.enum'
import {
  defaultPurposeForVendorsMap,
  PURPOSE_IS_SET_ENTRY_ID,
  PurposeForVendorsMap,
} from './defaults/default-purpose-for-vendors.map'
import { isEmpty } from '../../utils/assertions/is-empty'

const OTTO_CMP_ID = 284
const CMP_VERSION = 2
const CONSENT_LANGUAGE = 'DE'
const PUBLISHER_COUNTRY_CODE = 'DE'
const IS_SERVICE_SPECIFIC = true

const CONSENT_SCREEN = 1

const GVL_BASE_URL = 'https:/*                                         */
const GVL_FILENAME = 'iab2V2Data.json'

export class CmpIabTcfApiService implements TcfApi {
  private tcModel: TCModel | undefined

  constructor(private readonly logger: Logger) {}

  async readVendorsConsents(
    iabConsentEncoded?: string
  ): Promise<Map<Vendor, boolean>> {
    const vendorsConsent = initDefaultTcfVendorsConsentsMap()
    if (!iabConsentEncoded) {
      return vendorsConsent
    }
    try {
      const tcModel = TCString.decode(iabConsentEncoded)

      for (const vendorId of vendorsConsent.keys()) {
        const consentGiven = tcModel.vendorConsents.has(vendorId)
        vendorsConsent.set(vendorId, consentGiven)
      }
      return vendorsConsent
    } catch (reason) {
      this.logger.logError(reason, {
        info: 'Error while readVendorsConsents',
        iabConsentEncoded,
        vendorsConsent: JSON.stringify(vendorsConsent),
      })
      return initDefaultTcfVendorsConsentsMap()
    }
  }

  /**
 *
 */
  public async readConsentsStates(
    iabConsentEncoded?: string
  ): Promise<PurposeForVendorsMap> {
    const consentsStates = this.createDefaultVendorStatesMap()
    let tcModel = await this.getTcModel()
    try {
      if (iabConsentEncoded) {
        tcModel = TCString.decode(iabConsentEncoded, tcModel)
      }

      consentsStates.forEach((consentForPurposeMap, purposeId) => {
        const isPurposeSet = tcModel.purposeConsents.has(purposeId)
        consentForPurposeMap.set(PURPOSE_IS_SET_ENTRY_ID, isPurposeSet)
        consentForPurposeMap.name = Purpose[purposeId]

        Object.values({
          ...tcModel.gvl.getVendorsWithConsentPurpose(purposeId),
          ...tcModel.gvl.getVendorsWithFlexiblePurpose(purposeId),
        })
          .map((vendor) => vendor.id)
          .forEach((vendorId) => {
            if (!isPurposeSet) {
              consentForPurposeMap.set(vendorId, false)
              return
            }
            const restrictionType =
              tcModel.publisherRestrictions.getRestrictionType(
                vendorId,
                purposeId
              )
            if (restrictionType == RestrictionType.NOT_ALLOWED) {
              consentForPurposeMap.set(vendorId, false)
            }
            consentForPurposeMap.set(vendorId, true)
          })
      })

      return consentsStates
    } catch (reason) {
      this.logger.logError(reason, {
        info: 'Error while readConsentsStates',
        iabConsentEncoded: iabConsentEncoded ?? 'undefined or null',
        consentsStates: isEmpty(consentsStates)
          ? 'undefined or null'
          : JSON.stringify(consentsStates),
      })
      return consentsStates
    }
  }

  private createDefaultVendorStatesMap = defaultPurposeForVendorsMap

  private async getTcModel(): Promise<TCModel> {
    if (!this.tcModel) {
      const tcModel = new TCModel()
      tcModel.cmpId = OTTO_CMP_ID
      tcModel.publisherCountryCode = PUBLISHER_COUNTRY_CODE
      tcModel.cmpVersion = CMP_VERSION
      tcModel.isServiceSpecific = IS_SERVICE_SPECIFIC
      tcModel.consentLanguage = CONSENT_LANGUAGE
      tcModel.consentScreen = CONSENT_SCREEN

      GVL.baseUrl = GVL_BASE_URL
      GVL.latestFilename = GVL_FILENAME

      tcModel.gvl = new GVL()
      await tcModel.gvl.readyPromise
      tcModel.gvl.narrowVendorsTo(vendors)

      const date = new Date()
      date.setHours(0, 0, 0, 0)
      tcModel.created = date
      tcModel.lastUpdated = date
      this.tcModel = tcModel
    }
    return this.tcModel
  }
}
